import React from 'react';
import { Navigate, useLocation } from 'react-router';
import { UserRole, AccountStatus } from '@sportspredict/shared';
import {
	ROUTES,
	ADMIN_ROUTES,
	UNAUTHENTICATED_ROUTES,
	REGISTERED_USER_ROUTES,
	PENDING_CONSENT_ROUTES,
	PUBLIC_ALWAYS_ACCESSIBLE_ROUTES,
} from '@/constants';

interface RedirectGuardProps {
	children: React.ReactNode;
	isRegisteredUser: boolean;
	user: { role?: string; account_status?: string } | undefined;
	isLoading: boolean;
}

export function RedirectGuard({ children, isRegisteredUser, user, isLoading }: RedirectGuardProps) {
	const { pathname, search } = useLocation();

	if (isLoading) return null;

	const role = user?.role;
	const accountStatus = user?.account_status;
	const isAdmin = role === UserRole.Admin;
	const isUser = isRegisteredUser;
	const isPendingConsent = accountStatus === AccountStatus.PendingConsent;

	const match = (routes: string[]) =>
		routes.some((r) => pathname === r || pathname.startsWith(r + '/'));

	const isAdminRoute = match(ADMIN_ROUTES);
	const isRegisteredRoute = match(REGISTERED_USER_ROUTES);
	const isUnauthRoute = match(UNAUTHENTICATED_ROUTES);
	const isPendingConsentRoute = match(PENDING_CONSENT_ROUTES);
	const isPublicAlwaysRoute = match(PUBLIC_ALWAYS_ACCESSIBLE_ROUTES);

	// Parent email deep-links (approve / cancel / revoke) must render regardless
	// of session — otherwise a logged-in tester/student is bounced to home.
	if (isPublicAlwaysRoute) {
		return <>{children}</>;
	}

	if (isPendingConsent) {
		if (isUnauthRoute || isPendingConsentRoute) {
			return <>{children}</>;
		}
		return <Navigate to={ROUTES.PARENT_EMAIL_VERIFICATION} replace />;
	}

	if (isAdminRoute) {
		if (!isAdmin) {
			return <Navigate to={ROUTES.HOME} replace />;
		}
		return <>{children}</>;
	}

	if (isRegisteredRoute) {
		if (!isUser) {
			return <Navigate to={ROUTES.HOME} replace />;
		}
		return <>{children}</>;
	}

	if (isPendingConsentRoute) {
		if (!isPendingConsent) {
			return <Navigate to={ROUTES.HOME} replace />;
		}
		return <>{children}</>;
	}

	if (isUnauthRoute) {
		if (isUser || isAdmin) {
			const redirect = new URLSearchParams(search).get('redirect');
			return <Navigate to={redirect || ROUTES.HOME} replace />;
		}
		return <>{children}</>;
	}

	return <>{children}</>;
}
